import './voordelen-slider.scss';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin( Draggable, InertiaPlugin );

const VISIBLE = 5;
const CENTER = 2;
const GAP = 11;
const AUTO_INTERVAL = 4000;

function getStates( track ) {
	const tw = track.offsetWidth;
	const bh = track.offsetHeight;

	if ( window.innerWidth <= 768 ) {
		const sideW = Math.floor( tw * 0.25 );
		const centerW = tw - 2 * sideW - 2 * GAP;
		return [
			{ left: -sideW - GAP, width: sideW, height: bh * 0.6, zIndex: 0, opacity: 0, imgOpacity: 0, bg: '#315e78', isSide: true },
			{ left: 0, width: sideW, height: bh * 0.75, zIndex: 1, opacity: 1, imgOpacity: 0, bg: '#1b445c', isSide: true },
			{ left: sideW + GAP, width: centerW, height: bh, zIndex: 3, opacity: 1, imgOpacity: 1, bg: '#315e78', isSide: false },
			{ left: sideW + GAP + centerW + GAP, width: sideW, height: bh * 0.75, zIndex: 1, opacity: 1, imgOpacity: 0, bg: '#1b445c', isSide: true },
			{ left: tw + GAP, width: sideW, height: bh * 0.6, zIndex: 0, opacity: 0, imgOpacity: 0, bg: '#315e78', isSide: true },
		];
	}

	const cw = ( tw - GAP * ( VISIBLE - 1 ) ) / VISIBLE;

	return [
		{ left: 0, width: cw, height: bh * 0.6, zIndex: 1, opacity: 1, imgOpacity: 0, bg: '#315e78' },
		{ left: cw + GAP, width: cw, height: bh * 0.8, zIndex: 2, opacity: 1, imgOpacity: 0, bg: '#1b445c' },
		{ left: 2 * ( cw + GAP ), width: cw, height: bh, zIndex: 3, opacity: 1, imgOpacity: 1, bg: '#315e78' },
		{ left: 3 * ( cw + GAP ), width: cw, height: bh * 0.8, zIndex: 2, opacity: 1, imgOpacity: 0, bg: '#1b445c' },
		{ left: 4 * ( cw + GAP ), width: cw, height: bh * 0.6, zIndex: 1, opacity: 1, imgOpacity: 0, bg: '#315e78' },
	];
}

function updateSlider( currentCards, states, immediate, customDuration ) {
	const duration = immediate ? 0 : ( customDuration || 0.8 );

	currentCards.forEach( ( card, index ) => {
		const state = states[ index ];
		const imgEl = card.querySelector( '.b-voordelenSlider__cardImage' );

		if ( ! state || index >= VISIBLE ) {
			gsap.to( card, {
				autoAlpha: 0,
				duration: immediate ? 0 : 0.4,
				ease: 'power3.inOut',
			} );
			card.classList.remove( 'is-side' );
			return;
		}

		card.classList.toggle( 'is-side', !! state.isSide );

		gsap.to( card, {
			left: state.left,
			width: state.width,
			height: state.height,
			zIndex: state.zIndex,
			autoAlpha: state.opacity,
			backgroundColor: state.bg,
			duration: duration,
			ease: 'expo.out',
			overwrite: 'auto',
		} );

		if ( imgEl ) {
			gsap.to( imgEl, {
				autoAlpha: state.imgOpacity,
				duration: immediate ? 0 : 0.6,
				ease: 'power2.inOut',
			} );
		}
	} );
}

function initVoordelenSlider() {
	const roots = document.querySelectorAll( '[data-component="voordelen-slider"]' );

	if ( ! roots.length ) {
		return;
	}

	roots.forEach( ( root ) => {
		const slider = root.querySelector( '[data-vs-slider]' );
		const track = root.querySelector( '[data-vs-track]' );
		const cards = Array.from( root.querySelectorAll( '[data-vs-card]' ) );

		if ( ! slider || ! track || cards.length < VISIBLE ) {
			return;
		}

		let states = getStates( track );
		let currentCards = [ ...cards ];
		let intervalId = null;
		let isRotating = false;
		let draggableInstance = null;

		function resetTimer() {
			clearInterval( intervalId );
			intervalId = setInterval( rotate, AUTO_INTERVAL );
		}

		function rotate() {
			if ( isRotating ) {
				return;
			}
			isRotating = true;
			clearInterval( intervalId );

			const exitingCard = currentCards.shift();
			currentCards.push( exitingCard );

			const tw = track.offsetWidth;
			const lastState = states[ VISIBLE - 1 ];

			gsap.set( exitingCard, {
				left: tw + 100,
				autoAlpha: 0,
				width: lastState.width,
				height: lastState.height,
			} );

			const exitImg = exitingCard.querySelector( '.b-voordelenSlider__cardImage' );
			if ( exitImg ) {
				gsap.set( exitImg, { autoAlpha: 0 } );
			}

			updateSlider( currentCards, states, false );

			gsap.delayedCall( 0.8, () => {
				isRotating = false;
				resetTimer();
			} );
		}

		function rotateBack( duration ) {
			duration = duration || 0.8;

			if ( isRotating ) {
				return;
			}
			isRotating = true;
			clearInterval( intervalId );

			const exitingCard = currentCards.pop();
			currentCards.unshift( exitingCard );

			const firstState = states[ 0 ];

			gsap.set( exitingCard, {
				left: -firstState.width - GAP,
				autoAlpha: 0,
				width: firstState.width,
				height: firstState.height,
			} );

			const exitImg = exitingCard.querySelector( '.b-voordelenSlider__cardImage' );
			if ( exitImg ) {
				gsap.set( exitImg, { autoAlpha: 0 } );
			}

			updateSlider( currentCards, states, false, duration );

			gsap.delayedCall( duration, () => {
				isRotating = false;
				resetTimer();
			} );
		}

		function navigateTo( card ) {
			const index = currentCards.indexOf( card );
			if ( index === CENTER ) {
				return;
			}

			clearInterval( intervalId );
			if ( isRotating ) {
				return;
			}

			if ( index < CENTER ) {
				const steps = CENTER - index;
				const dur = steps > 1 ? 0.35 : 0.8;
				rotateBack( dur );
				if ( steps > 1 ) {
					gsap.delayedCall( dur + 0.05, () => navigateTo( card ) );
				}
			} else {
				const steps = index - CENTER;
				const dur = steps > 1 ? 0.35 : 0.8;
				rotate();
				if ( steps > 1 ) {
					gsap.delayedCall( 0.4, () => navigateTo( card ) );
				}
			}
		}

		updateSlider( currentCards, states, true );
		resetTimer();

		cards.forEach( ( card ) => {
			card.addEventListener( 'click', () => navigateTo( card ) );
		} );

		cards.forEach( ( card ) => {
			card.addEventListener( 'mouseenter', () => {
				clearInterval( intervalId );
			} );

			card.addEventListener( 'mouseleave', () => {
				resetTimer();
			} );
		} );

		const proxy = document.createElement( 'div' );

		const draggables = Draggable.create( proxy, {
			trigger: slider,
			type: 'x',
			inertia: true,
			edgeResistance: 0.65,
			onPress: () => {
				clearInterval( intervalId );
				if ( ! isRotating ) {
					gsap.killTweensOf( currentCards );
				}
			},
			onDragEnd() {
				if ( isRotating ) {
					gsap.set( proxy, { x: 0 } );
					return;
				}

				const dragThreshold = 50;
				const endX = this.endX;

				if ( endX < -dragThreshold ) {
					rotate();
				} else if ( endX > dragThreshold ) {
					rotateBack();
				}

				gsap.set( proxy, { x: 0 } );
			},
		} );

		draggableInstance = draggables[ 0 ];

		let resizeTimer = null;
		const handleResize = () => {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( () => {
				states = getStates( track );
				updateSlider( currentCards, states, true );
			}, 200 );
		};
		window.addEventListener( 'resize', handleResize );

		root._voordelenSliderCleanup = () => {
			clearInterval( intervalId );
			clearTimeout( resizeTimer );
			window.removeEventListener( 'resize', handleResize );
			if ( draggableInstance ) {
				draggableInstance.kill();
			}
		};
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initVoordelenSlider );
} else {
	initVoordelenSlider();
}
